/**
 * Frontend Debug Utility
 * 
 * Category-based debug logging system controlled by system settings.
 * Only logs when:
 * 1. Global debug flag is enabled (settings.debug = true), OR
 * 2. Specific category is enabled (settings.debug_categories.{category} = true)
 * 
 * Usage:
 *   import debug from '../utilities/debug';
 *   
 *   // Category-based logging (controlled by debug_categories)
 *   debug.log('openai', 'KB context built', { tokens: 1500 });
 *   debug.info('sms', 'Message sent', { to: '+1234567890' });
 *   
 *   // Always shown (not category-gated)
 *   debug.warn('Invalid response format', response);
 *   debug.error('API request failed', error);
 * 
 * Categories (from system_settings.debug_categories.frontend):
 *   - ui: UI components and rendering
 *   - api: API calls and responses
 *   - sms: SMS functionality
 *   - ably: Ably real-time messaging
 *   - admin: Admin panel operations
 *   - forms: Form handling and validation
 *   - redux: Redux state management
 *   - openai: OpenAI/LLM interactions
 *   - router: React Router navigation
 *   - cognito: Cognito authentication
 *   - storage: Local/session storage, IndexedDB
 *   - calendar: Calendar operations
 *   - webhooks: Webhook handling
 *   - analytics: Analytics tracking
 *   - documents: Document management
 *   - authentication: Auth flows
 *   - knowledge_base: KB queries and AI responses
 *   - memory_notifications: Memory notification system
 * 
 * Note: Settings are fetched from Redux store and cached for 30 seconds.
 */

import { store } from '../store';

let cachedSettings = null;
let cacheTimestamp = null;
const CACHE_TTL = 30000; // 30 seconds

/**
 * Get debug settings from Redux store with caching
 */
function getDebugSettings() {
  const now = Date.now();
  
  // Return cached settings if still valid
  if (cachedSettings && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
    return cachedSettings;
  }
  
  // Fetch fresh settings from Redux store
  try {
    const state = store.getState();
    const settings = state?.session?.settings || {};
    
    // Support nested structure: debug_categories.frontend
    const frontendCategories = settings.debug_categories?.frontend || settings.debug_categories || {};
    
    cachedSettings = {
      globalDebug: settings.debug === true,
      categories: frontendCategories
    };
    cacheTimestamp = now;
    
    return cachedSettings;
  } catch (error) {
    // If store not available, default to disabled
    return {
      globalDebug: false,
      categories: {}
    };
  }
}

/**
 * Check if logging is enabled for a specific category
 */
function isEnabled(category) {
  const settings = getDebugSettings();
  
  // If global debug is on, all categories are enabled
  if (settings.globalDebug) {
    return true;
  }
  
  // Otherwise check specific category
  return settings.categories[category] === true;
}

/**
 * Category-based debug log - only shown if category is enabled
 */
function log(category, message, data) {
  if (!isEnabled(category)) {
    return;
  }
  
  const prefix = `[DEBUG:${category}]`;
  
  if (data !== undefined) {
    console.log(prefix, message, data);
  } else {
    console.log(prefix, message);
  }
}

/**
 * Category-based info log - only shown if category is enabled
 */
function info(category, message, data) {
  if (!isEnabled(category)) {
    return;
  }
  
  const prefix = `[INFO:${category}]`;
  
  if (data !== undefined) {
    console.info(prefix, message, data);
  } else {
    console.info(prefix, message);
  }
}

/**
 * Warning log - always shown (not category-gated)
 * Use for important warnings that should always be visible
 */
function warn(message, data) {
  if (data !== undefined) {
    console.warn('[WARN]', message, data);
  } else {
    console.warn('[WARN]', message);
  }
}

/**
 * Error log - always shown (not category-gated)
 * Use for errors that should always be visible
 */
function error(message, data) {
  // Format error objects properly
  if (data instanceof Error) {
    console.error('[ERROR]', message, {
      message: data.message,
      stack: data.stack,
      name: data.name
    });
  } else if (data !== undefined) {
    console.error('[ERROR]', message, data);
  } else {
    console.error('[ERROR]', message);
  }
}

/**
 * Clear the debug settings cache
 * Useful after settings are updated
 */
function clearCache() {
  cachedSettings = null;
  cacheTimestamp = null;
}

/**
 * Get list of all available categories
 * Useful for debugging and displaying enabled categories
 */
function getCategories() {
  const settings = getDebugSettings();
  return Object.keys(settings.categories);
}

/**
 * Get current debug status
 * Returns object with globalDebug flag and enabled categories
 */
function getStatus() {
  const settings = getDebugSettings();
  const enabledCategories = Object.entries(settings.categories)
    .filter(([_, enabled]) => enabled === true)
    .map(([category, _]) => category);
  
  return {
    globalDebug: settings.globalDebug,
    enabledCategories,
    allCategories: Object.keys(settings.categories)
  };
}

const debug = {
  log,
  info,
  warn,
  error,
  isEnabled,
  clearCache,
  getCategories,
  getStatus
};

export default debug;
